import React, { useState, useEffect, useCallback } from 'react';
import {
	Input,
	Typography,
	message,
	Tooltip,
	Pagination,
	Spin,
	Button,
	Empty,
	Avatar,
} from 'antd';
import AppHeader from '../../../components/Dashboard/AppHeader';
import userStore from '../../../zustand/store/userStore';
import { debounce } from '../../../helper';
import { BarChartOutlined } from '@ant-design/icons';
import {
	getAllStudentResultsAPI,
	getSingleStudentResultAPI,
	getStudentResultsAPI,
} from '../../../zustand/API';
import { useNavigate } from 'react-router';

const { Title } = Typography;

const ResultScreen = () => {
	const { user } = userStore();
	const [data, setData] = useState([]);
	const [searchText, setSearchText] = useState('');
	const [loading, setLoading] = useState(false);
	const [pagination, setPagination] = useState({
		current: 1,
		pageSize: 6,
		total: 0,
	});
	const navigate = useNavigate();

	const fetchResults = async (page = 1, search = '') => {
		setLoading(true);
		try {
			const result =
			user?.role === 'admin'
					? await getAllStudentResultsAPI({
						search,
						page,
						limit: pagination.pageSize,
					}) :
				user?.role === 'teacher'
					? await getStudentResultsAPI({
						email: user.email,
						search,
						page,
						limit: pagination.pageSize,
					})
					: await getSingleStudentResultAPI({
						email: user.email,
						search,
						page,
						limit: pagination.pageSize,
					});

			setData(result.data);
			setPagination((prev) => ({
				...prev,
				current: result.currentPage,
				total: result.totalResults,
			}));
		} catch (err) {
			message.error('Failed to load quiz results.');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchResults(pagination.current);
	}, []);

	const debouncedSearch = useCallback(
		debounce((value) => {
			fetchResults(1, value);
		}, 1000),
		[pagination.pageSize],
	);

	const handleSearchChange = (e) => {
		const value = e.target.value;
		setSearchText(value);
		if (value.trim() === '') {
			fetchResults(1, '');
		} else {
			debouncedSearch(value);
		}
	};

	const handlePageChange = (page) => {
		setPagination((prev) => ({ ...prev, current: page }));
		fetchResults(page, searchText);
	};

	const handleQuizReport = (report) => {
		navigate('/view-chapter-report', {
			state: { report },
		});
	};

	return (
		<div className="min-h-screen p-2">
			<AppHeader
				title="Result Dashboard"
				content="Track your performance in quizzes across subjects and chapters."
			/>

			<div className="bg-white shadow-xl rounded-2xl p-6 mt-6">
				<div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6">
					<Title level={4} className="!mb-2 sm:!mb-0 !text-[#222]">
						{user?.role === 'teacher'
							? 'All Student Results'
							: 'Your Quiz History'}
					</Title>
					<Input
						placeholder="Search by subject or chapter"
						value={searchText}
						onChange={handleSearchChange}
						allowClear
						className="w-full sm:w-64 rounded-md"
					/>
				</div>
				{loading ? (
					<div className="text-center py-10 text-gray-500">
						<Spin />
					</div>
				) : data.length === 0 ? (
					<div className="text-center py-10">
						<Empty description="No results found." className="mt-10" />
					</div>
				) : (
					<>
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
							{data.map((result, index) => (
								<div
									key={index}
									className="bg-white border border-gray-200 rounded-xl shadow hover:shadow-lg transition-all duration-300 p-5"
								>
									<div className="flex items-center gap-3 mb-4">
										<Avatar
											style={{ backgroundColor: '#1890ff' }}
											size={40}
										>
											{result?.email?.charAt(0)?.toUpperCase() || '?'}
										</Avatar>

										<div className="min-w-0">
											<Tooltip title={result.email || 'No email'}>
												<p className="text-[15px] font-medium text-gray-800 max-w-[180px] truncate cursor-pointer">
													{result.email}
												</p>
											</Tooltip>
											<p className="text-sm text-gray-500">
												{user?.role === 'teacher' ? 'Student' : 'Instructor'}
											</p>
										</div>
									</div>

									<div className="mb-2">
										<p className="text-xs text-gray-500">Subject</p>
										<p className="text-sm font-medium text-gray-800">
											{result.course}
										</p>
									</div>

									<div className="mb-2">
										<p className="text-xs text-gray-500">Chapter</p>
										<p className="text-sm text-gray-700">{result.quizTaken}</p>
									</div>

									<div className="grid grid-cols-3 text-center my-4">
										<div>
											<p className="text-xs text-gray-500">Total</p>
											<p className="font-bold text-gray-700">
												{result.totalQuestions}
											</p>
										</div>
										<div>
											<p className="text-xs text-gray-500">Correct</p>
											<p className="font-bold text-blue-600">{result.marks}</p>
										</div>
										<div>
											<p className="text-xs text-gray-500">Percent</p>
											<p className="font-bold text-purple-600">
												{result.percentage}%
											</p>
										</div>
									</div>
									<div className="flex justify-between">
										<Tooltip title="Quiz Report">
											<Button
												type="text"
												icon={<BarChartOutlined />}
												className="text-blue-600 hover:bg-red-50 rounded-full"
												onClick={() =>
													handleQuizReport({
														email:
															(user?.role == 'teacher' || user?.role == "admin")
																? result.email
																: user?.email,
														subjectName: result.course,
														title: result.quizTaken,
													})
												}
											/>
										</Tooltip>
										<div
											className={`flex items-center px-3 py-1 rounded-full text-xs font-semibold ${result.status === 'Pass'
													? 'bg-green-100 text-green-600'
													: result.status === 'Need Improvment'
														? 'bg-yellow-100 text-yellow-600'
														: 'bg-red-100 text-red-600'
												}`}
										>
											{result.status}
										</div>
									</div>
								</div>
							))}
						</div>

						<div className="text-center">
							<Pagination
								current={pagination.current}
								pageSize={pagination.pageSize}
								total={pagination.total}
								onChange={handlePageChange}
								showSizeChanger={false}
							/>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export default ResultScreen;
